import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNetworkInfo } from '@/hooks/useNetworkInfo';

export type ImageQualityMode = 'data-saver' | 'balanced' | 'high-quality' | 'automatic';

export interface QualityMultipliers {
  qualityMultiplier: number;
  sizeMultiplier: number;
}

/**
 * Hook to manage user's image quality preference with automatic network detection
 */
export const useImageQuality = () => {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const { connectionType, saveData } = useNetworkInfo();
  const [qualityMode, setQualityMode] = useState<ImageQualityMode>('balanced');
  const [loading, setLoading] = useState(false);

  // Sync with user profile
  useEffect(() => {
    if (userProfile?.image_quality_preference) {
      setQualityMode(userProfile.image_quality_preference as ImageQualityMode);
    }
  }, [userProfile]);

  /**
   * Get the effective quality mode (resolves 'automatic' to actual mode)
   */
  const getEffectiveMode = (): Exclude<ImageQualityMode, 'automatic'> => {
    if (qualityMode !== 'automatic') {
      return qualityMode;
    }

    // Automatic mode: choose based on network
    if (saveData) {
      return 'data-saver'; // User has data saver enabled in browser
    }

    switch (connectionType) {
      case 'cellular':
        return 'data-saver'; // Use data saver on cellular
      case 'wifi':
        return 'balanced'; // Use balanced on WiFi
      default:
        return 'balanced'; // Default to balanced if unknown
    }
  };

  /**
   * Get quality multipliers for compression based on user preference and network
   */
  const getQualityMultipliers = (): QualityMultipliers => {
    const effectiveMode = getEffectiveMode();
    
    switch (effectiveMode) {
      case 'data-saver':
        return {
          qualityMultiplier: 0.7,  // Reduce quality by 30%
          sizeMultiplier: 0.6      // Target 60% of normal size
        };
      case 'high-quality':
        return {
          qualityMultiplier: 1.1,  // Increase quality by 10%
          sizeMultiplier: 1.5      // Allow 50% larger files
        };
      case 'balanced':
      default:
        return {
          qualityMultiplier: 1.0,  // Standard quality
          sizeMultiplier: 1.0      // Standard size
        };
    }
  };

  /**
   * Update user's quality preference in database
   */
  const updateQualityMode = async (mode: ImageQualityMode) => {
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ image_quality_preference: mode })
        .eq('id', user.id);

      if (error) throw error;

      setQualityMode(mode);
      
      const modeLabels = {
        'data-saver': 'Data Saver',
        'balanced': 'Balanced',
        'high-quality': 'High Quality',
        'automatic': 'Automatic'
      };

      toast({
        title: "Quality Setting Updated",
        description: `Image quality set to ${modeLabels[mode]}`,
      });
    } catch (error) {
      console.error('Error updating quality mode:', error);
      toast({
        title: "Error",
        description: "Failed to update quality setting",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    qualityMode,
    effectiveMode: getEffectiveMode(),
    connectionType,
    updateQualityMode,
    getQualityMultipliers,
    loading
  };
};
